import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import Modal from '@/components/modal/Modal.tsx';
import ModalHeader from '@/components/modal/ModalHeader.tsx';
import ModalContent from '@/components/modal/ModalContent.tsx';
import ModalFooter from '@/components/modal/ModalFooter.tsx';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import RichTextEditor from '@/components/richText/RichTextEditor.tsx';
import InputLabel from '@/components/form/base/InputLabel.tsx';
import InputError from '@/components/form/base/InputError.tsx';
import useSetCashInstruction from '@/features/dashboard/paymentConfig/hooks/useSetCashInstruction.ts';
import {
  cashInstructionSchema,
  CashInstructionFormData,
} from '@/features/dashboard/paymentConfig/schemas/cashInstructionSchema.ts';

interface EditCashInstructionModalProps {
  currentInstruction: string;
  onClose: () => void;
}

const EditCashInstructionModal = ({
  currentInstruction,
  onClose,
}: EditCashInstructionModalProps) => {
  const setMutation = useSetCashInstruction();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<CashInstructionFormData>({
    resolver: zodResolver(cashInstructionSchema),
    defaultValues: { instruction: currentInstruction },
  });

  const onSubmit = (data: CashInstructionFormData) => {
    setMutation.mutate(data, { onSuccess: () => onClose() });
  };

  return (
    <Modal onClose={onClose} className="max-w-[720px]">
      <form
        onSubmit={(e) => {
          e.stopPropagation();
          handleSubmit(onSubmit)(e);
        }}
      >
        <ModalHeader
          title="Edytuj instrukcję płatności gotówką"
          onClose={onClose}
        />
        <ModalContent>
          <div>
            <InputLabel label="Instrukcja" required />
            <Controller
              control={control}
              name="instruction"
              render={({ field }) => (
                <RichTextEditor
                  content={field.value ?? ''}
                  onChange={field.onChange}
                  htmlMode
                  error={errors.instruction?.message}
                />
              )}
            />
            <InputError error={errors.instruction?.message} />
          </div>
        </ModalContent>
        <ModalFooter>
          <ActionButton tone="ghost" size="sm" type="button" onClick={onClose}>
            Anuluj
          </ActionButton>
          <ActionButton
            tone="accent"
            size="sm"
            type="submit"
            isLoading={setMutation.isPending}
          >
            Zapisz
          </ActionButton>
        </ModalFooter>
      </form>
    </Modal>
  );
};

export default EditCashInstructionModal;
